import type { DatabaseDriver } from '@w/db'

export interface RecoveryStatement { sql: string; params?: unknown[]; meta?: { pageId: string } }
export interface CustomRecoveryPayload {
  tableName: string
  pageId?: string
  program: readonly RecoveryStatement[]
  beforeImages: readonly RecoveryStatement[]
}
export interface SyncProblem { id: string; statements: string[]; createdAt: number; reason: string }
interface DeadLetterRow { local_txn_id: number | bigint; program: string; created_at: number; reason: string }
interface ReplayDriver { reapply(program: readonly RecoveryStatement[], complete: (tx: DatabaseDriver) => Promise<void>): Promise<void> }
interface ReactiveDriver extends DatabaseDriver { subscribe?(sql: string, invalidate: () => void): Promise<() => void> | (() => void) }
interface ProblemSubscription { invalidate: () => void; release?: Promise<() => void>; stop?: () => void; closed: boolean }

/** Typed JSON is shared with the engine's recovery statement decoder. */
export function encodeRecoveryJson(value: unknown): string {
  function encode(item: unknown): unknown {
    if (typeof item === 'bigint') return { type: 'integer', value: String(item) }
    if (item instanceof Uint8Array) return { type: 'blob', value: Array.from(item) }
    if (Array.isArray(item)) return item.map(entry => encode(entry))
    if (item && typeof item === 'object') return Object.fromEntries(Object.entries(item).map(([key, entry]) => [key, encode(entry)]))
    return item
  }
  return JSON.stringify(encode(value))
}
function decodeProgram(program: string): RecoveryStatement[] {
  const value: unknown = JSON.parse(program, (_key, item: unknown) => {
    if (item && typeof item === 'object' && 'type' in item && 'value' in item) {
      if (item.type === 'integer' && typeof item.value === 'string') return BigInt(item.value)
      if (item.type === 'blob' && Array.isArray(item.value)) return Uint8Array.from(item.value as number[])
    }
    return item
  })
  if (!Array.isArray(value) || value.some(statement => !statement || typeof statement !== 'object' || typeof statement.sql !== 'string')) throw new Error('Invalid custom database recovery program')
  return value as RecoveryStatement[]
}

/** The latest CDC row is this active transaction's last captured write. A
 * preceding commit marker identifies a zero-effect transaction. The exclusive
 * driver lease prevents another local writer on this physical connection.
 */
export async function cdbPreCommit(tx: DatabaseDriver, payload: CustomRecoveryPayload): Promise<void> {
  const [last] = await tx.query<{ change_txn_id: number | bigint; change_type: number }>('SELECT change_txn_id,change_type FROM turso_cdc ORDER BY change_id DESC LIMIT 1')
  if (!last || last.change_type === 2 || last.change_txn_id < 0) return
  await tx.exec('CREATE TABLE IF NOT EXISTS _turso_replication_recovery(local_txn_id INTEGER PRIMARY KEY,page_id TEXT NOT NULL,table_name TEXT NOT NULL,program TEXT NOT NULL,before_images TEXT NOT NULL,created_at INTEGER NOT NULL,publication_sequence INTEGER)')
  await tx.exec('CREATE TABLE IF NOT EXISTS _turso_replication_dead_letters(local_txn_id INTEGER PRIMARY KEY,page_id TEXT NOT NULL,table_name TEXT NOT NULL,program TEXT NOT NULL,before_images TEXT NOT NULL,created_at INTEGER NOT NULL,publication_sequence INTEGER,reason TEXT NOT NULL)')
  await tx.exec('INSERT INTO _turso_replication_recovery(local_txn_id,page_id,table_name,program,before_images,created_at) VALUES(?,?,?,?,?,?)',
    [last.change_txn_id, payload.pageId ?? '', payload.tableName, encodeRecoveryJson(payload.program), encodeRecoveryJson(payload.beforeImages), Date.now()])
}

export function createDeadLetterService(options: { driver: ReactiveDriver; customDriver: ReplayDriver; afterCommit?(): Promise<void> }) {
  const { driver, customDriver } = options
  const listSql = 'SELECT local_txn_id,program,created_at,reason FROM _turso_replication_dead_letters ORDER BY created_at,local_txn_id'
  const schemaSql = "SELECT name FROM sqlite_schema WHERE name='_turso_replication_dead_letters'"
  const subscriptions = new Set<ProblemSubscription>()
  async function attach(subscription: ProblemSubscription) {
    if (!driver.subscribe) throw new Error('Workspace driver must support sync problem subscriptions')
    subscription.release ??= Promise.resolve(driver.subscribe(listSql, subscription.invalidate))
    const release = await subscription.release
    subscription.stop = release
    if (subscription.closed) release()
  }
  function key(id: string) {
    if (!/^-?\d+$/u.test(id)) throw new Error('Invalid sync problem identity')
    return BigInt(id)
  }
  async function list(): Promise<SyncProblem[]> {
    if ((await driver.query(schemaSql)).length === 0) return []
    for (const subscription of subscriptions) await attach(subscription)
    const rows = await driver.query<DeadLetterRow>(listSql)
    return rows.map(row => ({ id: String(row.local_txn_id), statements: decodeProgram(row.program).map(statement => statement.sql), createdAt: row.created_at, reason: row.reason }))
  }
  function discard(id: string): Promise<void> {
    return driver.exec('DELETE FROM _turso_replication_dead_letters WHERE local_txn_id=?', [key(id)])
  }
  async function reapply(id: string): Promise<void> {
    const [entry] = await driver.query<DeadLetterRow>('SELECT local_txn_id,program,created_at,reason FROM _turso_replication_dead_letters WHERE local_txn_id=?', [key(id)])
    if (!entry) throw new Error('Sync problem no longer exists')
    const program = decodeProgram(entry.program)
    try {
      await customDriver.reapply(program, async tx => {
        const removed = await tx.query('DELETE FROM _turso_replication_dead_letters WHERE local_txn_id=? RETURNING local_txn_id', [key(id)])
        if (removed.length !== 1) throw new Error('Sync problem no longer exists')
      })
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error)
      try { await driver.exec('UPDATE _turso_replication_dead_letters SET reason=? WHERE local_txn_id=?', [reason, key(id)]) }
      catch (recordingError) { throw new SuppressedError(recordingError, error, 'Failed to record sync problem reapply failure') }
      return
    }
    await options.afterCommit?.()
  }
  async function subscribe(invalidate: () => void): Promise<() => void> {
    if (!driver.subscribe) throw new Error('Workspace driver must support sync problem subscriptions')
    using cleanup = new DisposableStack()
    const subscription: ProblemSubscription = { invalidate, closed: false }
    const releaseSchema = await driver.subscribe(schemaSql, invalidate)
    cleanup.defer(releaseSchema)
    subscriptions.add(subscription)
    cleanup.defer(() => {
      subscriptions.delete(subscription)
      subscription.closed = true
      // attach owns release even if list is still installing the subscription.
      subscription.stop?.()
    })
    if ((await driver.query(schemaSql)).length > 0) await attach(subscription)
    const owned = cleanup.move()
    return () => { owned.dispose() }
  }
  return { list, discard, reapply, subscribe }
}
export type DeadLetterService = ReturnType<typeof createDeadLetterService>
